import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, UserPlus, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getVid } from "@/lib/vid";

const BANNER = "/ant-banner.webp?v=2";

const field =
  "w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-4 py-3 text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14] transition-shadow";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({
    nom: "", prenom: "", sexe: "Homme", date_naissance: "",
    email: "", password: "", confirm: "", referral_code: "",
  });
  const [verifyEmail, setVerifyEmail] = useState("");

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
      setMode("register");
      api.post("/referral/track", { code: ref.toUpperCase(), vid: getVid() }).catch(() => {});
    }
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const resendVerification = async () => {
    try {
      await api.post("/auth/resend-verification", { email: verifyEmail });
      toast.success("Email de confirmation renvoyé. Vérifiez votre boîte mail (et vos spams).");
    } catch (err) {
      toast.error("Impossible de renvoyer l'email pour le moment.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("Si un compte existe, un email de réinitialisation vient d'être envoyé.");
        setMode("login");
        setLoading(false);
        return;
      }
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bienvenue sur A Nous Turf !");
        navigate("/jeu");
      } else {
        if (form.password !== form.confirm) {
          toast.error("Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }
        const { confirm, ...payload } = form;
        const r = await register(payload);
        if (r && r.pending_verification) {
          setVerifyEmail(r.email || form.email);
          toast.success("Compte créé ! Confirmez votre email pour activer votre compte.");
          setMode("login");
        } else {
          toast.success("Bienvenue sur A Nous Turf !");
          navigate("/jeu");
        }
      }
    } catch (err) {
      if (err.response?.status === 403) setVerifyEmail(form.email);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col ant-radial">
      {/* Full brand banner */}
      <div className="relative w-full bg-[#0B120E] border-b border-[#00E676]/15 leading-none">
        <img
          src={BANNER}
          alt="A Nous Turf — Le jeu N°1 des Turfistes"
          className="block w-full h-auto"
          data-testid="login-banner"
        />
        <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 z-20 rounded-3xl bg-[#0B120E]/85 backdrop-blur-md border border-[#00E676]/40 px-6 py-3 shadow-[0_0_35px_rgba(0,230,118,0.45)]" data-testid="login-emblem-bubble">
          <img
            src="/ant-emblem.png"
            alt="ANT Pronostics"
            data-testid="login-emblem"
            className="h-16 sm:h-24 w-auto object-contain drop-shadow-[0_0_18px_rgba(0,230,118,0.6)]"
          />
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 relative overflow-hidden flex items-start justify-center px-6 pb-6 pt-20 sm:px-10 sm:pt-24">
        <div className="ant-blob ant-blob-a w-[420px] h-[420px] bg-[#00E676]/25 -top-20 -left-16" />
        <div className="ant-blob ant-blob-b w-[360px] h-[360px] bg-[#39FF14]/20 bottom-0 right-0" />
        <div className="ant-blob ant-blob-a w-[300px] h-[300px] bg-[#00E676]/15 top-40 right-1/4" style={{ animationDelay: "3s" }} />
        <div className="relative z-10 w-full max-w-md ant-in">
          {stats && (
            <div className="flex items-center justify-center gap-3 mb-6" data-testid="public-stats">
              <div className="flex items-center gap-2 rounded-full bg-[#131F17] border border-[#00E676]/20 px-4 py-1.5">
                <Users size={15} className="text-[#00E676]" />
                <span className="tabular font-bold text-white">{stats.registered}</span>
                <span className="text-xs text-[#A1B5A8]">inscrit{stats.registered > 1 ? "s" : ""}</span>
              </div>
              <div className="flex items-center gap-2 rounded-full bg-[#131F17] border border-[#39FF14]/25 px-4 py-1.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#39FF14] opacity-70" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#39FF14]" />
                </span>
                <span className="tabular font-bold text-white">{stats.online}</span>
                <span className="text-xs text-[#A1B5A8]">en ligne</span>
              </div>
            </div>
          )}
          {mode === "forgot" ? (
            <div className="mb-8">
              <h2 className="text-2xl font-bold">Mot de passe oublié ?</h2>
              <p className="text-sm text-[#A1B5A8] mt-1">Entrez votre adresse mail, nous vous enverrons un lien pour réinitialiser votre mot de passe.</p>
            </div>
          ) : (
            <div className="flex bg-[#131F17] rounded-xl p-1 mb-8">
              <button
                onClick={() => setMode("login")}
                data-testid="tab-login"
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-[#00E676] text-[#04180a]" : "text-[#A1B5A8]"}`}
              >
                <LogIn size={16} /> Connexion
              </button>
              <button
                onClick={() => setMode("register")}
                data-testid="tab-register"
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-[#00E676] text-[#04180a]" : "text-[#A1B5A8]"}`}
              >
                <UserPlus size={16} /> Inscription
              </button>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <input className={field} placeholder="Prénom" required value={form.prenom} onChange={set("prenom")} data-testid="input-prenom" />
                  <input className={field} placeholder="Nom" required value={form.nom} onChange={set("nom")} data-testid="input-nom" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select className={field} value={form.sexe} onChange={set("sexe")} data-testid="input-sexe">
                    <option>Homme</option>
                    <option>Femme</option>
                    <option>Autre</option>
                  </select>
                  <input className={field} type="date" required value={form.date_naissance} onChange={set("date_naissance")} data-testid="input-naissance" />
                </div>
              </>
            )}
            <input className={field} type="email" placeholder="Adresse mail (identifiant)" required value={form.email} onChange={set("email")} data-testid="input-email" />
            {mode !== "forgot" && (
              <input className={field} type="password" placeholder="Mot de passe" required value={form.password} onChange={set("password")} data-testid="input-password" />
            )}
            {mode === "register" && (
              <input className={field} type="password" placeholder="Confirmer le mot de passe" required value={form.confirm} onChange={set("confirm")} data-testid="input-confirm" />
            )}
            {mode === "register" && (
              <input className={field} placeholder="Code de parrainage (optionnel)" value={form.referral_code} onChange={set("referral_code")} data-testid="input-referral" />
            )}

            {mode === "login" && (
              <div className="text-right -mt-1">
                <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-link" className="text-xs text-[#39FF14] hover:underline">
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            {verifyEmail && (
              <div className="rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/5 p-3 text-xs text-[#E7F5EC]" data-testid="verify-banner">
                Votre compte n'est pas encore confirmé. Cliquez sur le lien envoyé à <b className="text-[#FFD700]">{verifyEmail}</b> (pensez à vérifier vos spams).
                <button type="button" onClick={resendVerification} data-testid="resend-verification-btn" className="mt-2 w-full py-2 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#FFD700] to-[#FFC400]">
                  Renvoyer l'email de confirmation
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-btn"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] ant-glow hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Se connecter" : mode === "forgot" ? "Envoyer le lien" : "Créer mon compte"}
            </button>

            {mode === "forgot" && (
              <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-[#A1B5A8] hover:text-white">
                ← Retour à la connexion
              </button>
            )}
          </form>

          <p className="mt-6 text-center text-xs text-[#697C6F]">
            En vous connectant, vous rejoignez la communauté des turfistes ANT.
          </p>
          <p className="mt-2 text-center text-xs">
            <Link to="/politique-confidentialite" data-testid="privacy-link" className="text-[#697C6F] hover:text-[#39FF14] underline underline-offset-2">
              Politique de confidentialité
            </Link>
            <span className="text-[#2d3b32] mx-2">·</span>
            <Link to="/mentions-legales" data-testid="legal-link" className="text-[#697C6F] hover:text-[#39FF14] underline underline-offset-2">
              Mentions légales / CGU
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
